import React, {useState} from 'react';
import {
    addDays,
    isSameDay,
    isBefore,
    startOfMonth,
    differenceInDays,
    differenceInCalendarDays,
    format,
    addMonths,
    endOfMonth,
    startOfDay,
    isAfter,
    isLastDayOfMonth
} from 'date-fns';
import {uk} from 'date-fns/locale';
import {
    Popper,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Divider,
    TextField,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    useMediaQuery
} from '@mui/material';
import DoDisturbOffIcon from '@mui/icons-material/DoDisturbOff';
import LockIcon from '@mui/icons-material/Lock';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AirplanemodeInactiveIcon from '@mui/icons-material/AirplanemodeInactive';
import {useNavigate} from 'react-router-dom';
import {flattenObjects, isActiveVisualBooking} from './calendarSingle';
import AuthProvider from "../hooks/authProvider";
import jsonToFormData from "../hooks/jsonToFormData";
import api from '../hooks/useApi';
import ModalForm from '../components/forms/modalFormAlt';
import {showSnackbar} from "./snackbarUtils";

// Функция для генерации массива дат между двумя датами
const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    let currentDate = startDate;
    while (!isAfter(currentDate, endDate)) {
        dates.push(currentDate);
        currentDate = addDays(currentDate, 1);
    }
    return dates;
};

// Try to derive full range from URL params (booking_from, booking_to). Returns null if absent/invalid.
const getFilterDatesFromURL = () => {
    try {
        const sp = new URLSearchParams(window.location.search);
        const from = sp.get('booking_from');
        const to = sp.get('booking_to');
        if (!from || !to) return null;
        const s = parseBookingCalendarDay(from);
        const eIncl = parseBookingCalendarDay(to); // treat filter "to" as inclusive for the grid
        if (isBefore(eIncl, s)) return null;
        return getDatesBetween(s, eIncl);
    } catch (e) {
        return null;
    }
};

// ===== Overbooking lane helpers (allow up to 5+ parallel overbookings) =====

const parseBookingCalendarDay = (value) => {
    if (!value) return null;
    const normalized = String(value).trim();
    const m = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const localDay = new Date(year, month - 1, day);
    if (
        Number.isNaN(localDay.getTime()) ||
        localDay.getFullYear() !== year ||
        localDay.getMonth() !== month - 1 ||
        localDay.getDate() !== day
    ) {
        return null;
    }
    return startOfDay(localDay);
};

// Visual booking range is [from, to), except same-day booking which occupies exactly one day.
// 01→05 => 01..04, 01→02 => 01, 10 21:00 → 10 21:00 => 10
const getVisualBookingRange = (booking_from, booking_to) => {
    const startDay = parseBookingCalendarDay(booking_from);
    const exclusiveEndDay = parseBookingCalendarDay(booking_to);
    if (!startDay || !exclusiveEndDay || isBefore(exclusiveEndDay, startDay)) {
        return {startDay: null, visualEndDay: null, exclusiveEndDay: null, valid: false};
    }
    const visualEndDay = isSameDay(startDay, exclusiveEndDay)
        ? exclusiveEndDay
        : addDays(exclusiveEndDay, -1);
    return {startDay, visualEndDay, exclusiveEndDay, valid: true};
};

const segmentIndices = (b, allDates) => {
    if (!Array.isArray(allDates) || allDates.length === 0) return [-1, -1];
    const range = getVisualBookingRange(b.booking_from, b.booking_to);
    if (!range.valid) return [-1, -1];
    const s = range.startDay;
    const inclusiveEnd = range.visualEndDay;

    const dayKey = (d) => format(d, 'yyyy-MM-dd');
    const firstDate = allDates[0];
    const lastDate = allDates[allDates.length - 1];

    if (isAfter(s, lastDate) || isBefore(inclusiveEnd, firstDate)) {
        return [-1, -1];
    }

    let startIdx;
    if (isBefore(s, firstDate)) {
        startIdx = 0;
    } else {
        startIdx = allDates.findIndex((d) => dayKey(d) === dayKey(s));
        if (startIdx === -1) startIdx = 0;
    }

    let endIdx;
    if (isAfter(inclusiveEnd, lastDate)) {
        endIdx = allDates.length - 1;
    } else {
        endIdx = allDates.findIndex((d) => dayKey(d) === dayKey(inclusiveEnd));
        if (endIdx === -1) endIdx = startIdx;
    }

    if (endIdx < startIdx) return [-1, -1];
    return [startIdx, endIdx];
};

// Greedy lane assignment: color intervals into minimal number of lanes
const buildOverbookingLanes = (segments, allDates) => {
    // segment: { id, booking_from, booking_to, first_name, last_name }
    const items = segments
        .map((b) => {
            if (typeof b._start === 'number' && typeof b._end === 'number') {
                return b;
            }
            const [start, end] = segmentIndices(b, allDates);
            return {...b, _start: start, _end: end};
        })
        .filter((it) => it._start >= 0 && it._end >= it._start)
        .sort((a, b) => a._start - b._start || a._end - b._end);

    const lanes = []; // each lane is an array of items
    const laneEnds = []; // laneEnds[i] = last end index occupied in lane i

    items.forEach((it) => {
        let placed = false;
        for (let i = 0; i < lanes.length; i++) {
            if (it._start > laneEnds[i]) { // strictly after previous segment in lane
                lanes[i].push(it);
                laneEnds[i] = it._end;
                placed = true;
                break;
            }
        }
        if (!placed) {
            lanes.push([it]);
            laneEnds.push(it._end);
        }
    });
    return lanes; // array of lanes; lanes.length is number of rows we render under object
};

// ===== Mobile optimized rows (touch-friendly, horizontal scroll) =====
const MobileRows = ({
                        data, allDates,
                        handleCellClick, handleLabelClick,
                        selectedCells, setSelectedCells,
                        setAnchorEl, setPopupMode,
                        setClickedBookingId, setSelectedObjId,
                        navigate, onOpenOverList,
                        openQuickBooking              // ← НОВЫЙ ПРОП
                    }) => {
    // If URL filter range is wider than provided allDates, expand to it (keeps existing behavior otherwise)
    (() => {
        const urlDates = getFilterDatesFromURL();
        if (urlDates && urlDates.length > (allDates?.length || 0)) {
            // reassign param on purpose to keep all downstream code intact
            // eslint-disable-next-line no-param-reassign
            allDates = urlDates;
        }
    })();
    const mod_data = flattenObjects(data);
    const toStr = (v) => (v != null ? String(v) : '');
    const isOver = (b) => toStr(b?.status) === '6' || toStr(b?.booking_system_from) === '4';
    const isExternal = (b) => toStr(b?.booking_system_from) === '2' || toStr(b?.booking_system_from) === '3';
    const getRealSegments = (bookings, dates) => {
        return (bookings || [])
            .filter(isActiveVisualBooking)
            .map((b) => {
                const [start, end] = segmentIndices(b, dates);
                return {...b, _start: start, _end: end};
            })
            .filter((b) => b._start >= 0 && b._end >= b._start)
            .sort((a, b) => a._start - b._start || a._end - b._end);
    };
    // === touch-drag helpers (geometry-based date pick) ===
    const isDraggingRef = React.useRef(false);

    const [dragging, setDragging] = React.useState(false);

    // Touch helpers: map a touch/click target to cell meta
    const getMetaFromEvent = (ev) => {
        const el = ev.target.closest('[data-objid][data-date]');
        if (!el) return null;
        return {
            el,
            objId: el.getAttribute('data-objid'),
            date: el.getAttribute('data-date'),
            type: el.getAttribute('data-type') || ''
        };
    };
    const isInteractiveTap = (ev) => {
        const el = ev.target;
        return !!(el.closest('[data-bookid]') || el.closest('[data-over="1"]'));
    };

    return (
        <>
            {mod_data.map((obj, idx) => {
                const objIdStr = String(obj.id);
                // const isSelected = selectedCells.some(
                //     cell => cell.date === format(date, 'yyyy-MM-dd') && String(cell.objId) === String(obj.id)
                // );

// ---- selection band helpers (per object) ----
                const dayKey = (d) => format(d, 'yyyy-MM-dd');
                const selDatesForObj = selectedCells.filter(c => String(c.objId) === objIdStr).map(c => c.date);
                const selSet = new Set(selDatesForObj);
                const selIndices = allDates.map((d, i) => selSet.has(dayKey(d)) ? i : null).filter(i => i !== null);
                const selStartIdx = selIndices.length ? selIndices[0] : -1;
                const selEndIdx = selIndices.length ? selIndices[selIndices.length - 1] : -1;
                const realSegments = getRealSegments(obj.bookings, allDates);
                const realStarts = new Map(realSegments.map((b) => [b._start, b]));
                const coveredRealDays = new Set();
                realSegments.forEach((b) => {
                    for (let i = b._start + 1; i <= b._end; i++) {
                        coveredRealDays.add(i);
                    }
                });
                if (obj.children_obj?.length > 0) {
                    return (
                        <div key={`grp-${idx}`} style={{background: '#EEEEEE', padding: '8px 12px'}}>
                            <div
                                onClick={(ev) => handleLabelClick(ev, obj.id)}
                                style={{textDecoration: 'underline', cursor: 'pointer', fontWeight: 500}}
                            >
                                {obj.name} <span style={{fontSize: '0.85em', opacity: 0.8}}>({obj.service_name})</span>
                            </div>
                        </div>
                    );
                }


                // Mobile row: sticky title + horizontally scrollable day lane
                return (
                    <div key={`row-${obj.id}`} style={{borderBottom: '1px solid #eee'}}>
                        <div
                            className='object_title'
                            data-objid={obj.id}
                            style={{
                                padding: '10px 12px',
                                position: 'sticky',
                                top: 0,
                                background: 'white',
                                zIndex: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                            onClick={(ev) => obj.ctype === 'hotel' && handleLabelClick(ev, obj.id)}
                        >
              <span>
                {obj.title || obj.name} <span style={{fontSize: '0.85em', opacity: 0.8}}>({obj.service_name})</span>
              </span>
                            {/* Quick actions overflow kept via Popper; labels open info/calendar via existing flow */}
                        </div>

                        {/* Month bar */}
                        <div style={{display: 'flex', gap: 8, padding: '6px 12px', fontSize: 12, color: '#666'}}>
                            <span>{format(allDates[0], 'dd LLL', {locale: uk})}</span>
                            <span>→</span>
                            <span>{format(allDates[allDates.length - 1], 'dd LLL yyyy', {locale: uk})}</span>
                        </div>

                        {/* Scrollable grid */}
                        <div
                            className="mobileScroll"
                            style={{
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                padding: '0 8px 12px',
                                // если идёт drag — отключаем нативный скролл, чтобы move не прерывался
                                touchAction: dragging ? 'none' : 'pan-x'
                            }}
                            onTouchStart={(ev) => {
                                if (isInteractiveTap(ev)) return;
                                const meta = getMetaFromEvent(ev);
                                if (!meta) return;
                                isDraggingRef.current = true;
                                setDragging(true);
                                handleCellClick({currentTarget: meta.el}, meta.objId, 'start', meta.date);
                                ev.preventDefault();
                            }}
                            onTouchMove={(ev) => {
                                if (!isDraggingRef.current || isInteractiveTap(ev)) return;
                                const meta = getMetaFromEvent(ev);
                                if (!meta) return;
                                handleCellClick({currentTarget: meta.el}, meta.objId, 'move', meta.date);
                                ev.preventDefault();
                            }}
                            onTouchEnd={(ev) => {
                                if (isInteractiveTap(ev)) {
                                    // раньше тут был ранний return без сброса
                                    isDraggingRef.current = false;
                                    setDragging(false);
                                    return;
                                }
                                const meta = getMetaFromEvent(ev) || (selectedCells[0] && {
                                    el: ev.target.closest('[data-objid][data-date]') || ev.currentTarget,
                                    objId: selectedCells[0].objId,
                                    date: selectedCells[selectedCells.length - 1].date,
                                });
                                if (meta) {
                                    handleCellClick({currentTarget: meta.el}, meta.objId, 'end', meta.date);
                                }
                                isDraggingRef.current = false;
                                setDragging(false);
                                ev.preventDefault();
                            }}
                        >

                            <div style={{
                                display: 'grid',
                                gridAutoFlow: 'column',
                                gridAutoColumns: '44px',
                                gap: 4,
                                width: 'max-content'
                            }}>
                                {allDates.map((date, index) => {
                                    const real = realStarts.get(index);
                                    const isSelected = selectedCells.some(c => c.date === dayKey(date) && String(c.objId) === objIdStr);
                                    const baseStyle = {
                                        height: 40,
                                        borderRadius: 8,
                                        border: '1px solid #e0e0e0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                        position: 'relative',
                                        userSelect: 'none',
                                        touchAction: 'none'
                                    };

                                    if (coveredRealDays.has(index)) {
                                        return null;
                                    }
                                    if (!real) {
                                        return (
                                            <div
                                                key={dayKey(date)}
                                                data-objid={obj.id}
                                                data-type={obj.ctype}
                                                data-date={dayKey(date)}
                                                style={{
                                                    ...baseStyle,
                                                    background: isSelected ? '#E3F2FD' : '#fff',
                                                    boxShadow: isSelected ? 'inset 0 0 0 2px #90CAF9' : 'none',
                                                    borderLeftWidth: isSelected && index === selStartIdx ? 2 : 1,
                                                    borderRightWidth: isSelected && index === selEndIdx ? 2 : 1,
                                                    borderColor: isSelected ? '#90CAF9' : '#e0e0e0',
                                                    borderStyle: 'solid',
                                                    borderRadius: isSelected
                                                        ? (index === selStartIdx && index === selEndIdx ? 12
                                                            : index === selStartIdx ? '12px 0 0 12px'
                                                                : index === selEndIdx ? '0 12px 12px 0'
                                                                    : 0)
                                                        : 8
                                                }}
                                                onClick={(ev) => openQuickBooking(ev.currentTarget, obj.id, dayKey(date))}
                                            >
                                                {format(date, 'dd')}
                                            </div>
                                        );
                                    }

                                    const safeSpan = real._end - real._start + 1;

                                    const getMobileBookingColor = (booking) => {
                                        if (booking.is_overbooking) return '#009e3d';

                                        const systemFrom = String(booking.booking_system_from);

                                        if (systemFrom === '0') return '#ff0000';
                                        if (systemFrom === '3') return '#007BEE';
                                        if (systemFrom === '2') return '#FF9900';

                                        return '#fd0';
                                    };

                                    const color = getMobileBookingColor(real);

                                    return (
                                        <div
                                            key={`seg-${real.id}-${dayKey(date)}`}
                                            data-objid={obj.id}
                                            data-type={obj.ctype}
                                            data-date={dayKey(date)}
                                            data-bookid={real.id}
                                            style={{
                                                ...baseStyle,
                                                gridColumn: `span ${safeSpan}`,
                                                background: color,
                                                borderColor: color,
                                                justifyContent: 'flex-start',
                                                paddingLeft: 8,
                                                position: 'relative',
                                                overflow: 'hidden',
                                                boxShadow:
                                                    (selStartIdx !== -1 && index <= selEndIdx && (index + safeSpan - 1) >= selStartIdx)
                                                        ? 'inset 0 0 0 2px #90CAF9'
                                                        : 'none',
                                                height: 48, // Give room for bottom caption
                                            }}
                                            onClick={(ev) => {
                                                setAnchorEl(ev.currentTarget);
                                                setPopupMode(isExternal(real) ? 'booking-external' : 'booking');
                                                setClickedBookingId(real.id);
                                                setSelectedObjId(obj.id);
                                            }}
                                        >
                                            {/* Per-day numbers overlay across the spanned booking so dates remain visible on mobile */}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    display: 'grid',
                                                    gridTemplateColumns: `repeat(${safeSpan}, 1fr)`,
                                                    alignItems: 'center',
                                                    padding: '0 4px',
                                                    pointerEvents: 'none',
                                                    zIndex: 1
                                                }}
                                            >
                                                {Array.from({length: safeSpan}).map((_, i) => (
                                                    <div key={i} style={{
                                                        textAlign: 'center',
                                                        fontSize: 10,
                                                        opacity: 0.6,
                                                        color: 'rgba(0,0,0,0.85)'
                                                    }}>
                                                        {format(allDates[index + i], 'dd')}
                                                    </div>
                                                ))}
                                            </div>
                                            {/* Bottom ribbon label (guest or closed period) */}
                                            <div style={{
                                                position: 'absolute',
                                                left: 6,
                                                right: 6,
                                                bottom: 0.5,
                                                padding: '2px 6px',
                                                borderRadius: 4,
                                                background: 'rgba(255,255,255,0.85)',
                                                backdropFilter: 'saturate(150%) blur(2px)',
                                                fontSize: 12,
                                                lineHeight: 1.2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                zIndex: 3
                                            }}>
                                                {String(real.booking_system_from) === '0'
                                                    ? 'Період закрито'
                                                    : `${real.first_name} ${real.last_name}`}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Overbooking lanes (condensed as chips) */}
                            {(() => {
                                const rawOver = (obj.bookings || []).filter(isOver);
                                if (!rawOver.length) {
                                    return <div className="overbooking-row ghost" style={{height: 50}}/>;
                                }

                                const withIdx = rawOver
                                    .map((b) => {
                                        const [s, e] = segmentIndices(b, allDates);
                                        return {...b, _start: s, _end: e};
                                    })
                                    .filter((b) => b._start >= 0 && b._end >= b._start);

                                if (!withIdx.length) {
                                    return <div className="overbooking-row ghost" style={{height: 50}}/>;
                                }

                                const byPeriod = new Map();
                                withIdx.forEach((it) => {
                                    const key = `${it._start}-${it._end}`;
                                    const bucket = byPeriod.get(key) || {_start: it._start, _end: it._end, items: []};
                                    bucket.items.push(it);
                                    byPeriod.set(key, bucket);
                                });

                                const periodGroups = Array.from(byPeriod.values())
                                    .sort((a, b) => a._start - b._start || a._end - b._end);

                                const lanes = buildOverbookingLanes(periodGroups.map(g => ({
                                    id: g.items[0]?.id,
                                    booking_from: g.items[0]?.booking_from,
                                    booking_to: g.items[0]?.booking_to,
                                    _start: g._start,
                                    _end: g._end,
                                    _group: g
                                })), allDates);

                                if (!lanes.length) {
                                    return <div className="overbooking-row ghost" style={{height: 50}}/>;
                                }

                                return (
                                    <div style={{display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6}}>
                                        {lanes.map((lane, laneIdx) => (
                                            <div
                                                key={`overlane-${obj.id}-${laneIdx}`}
                                                className="overbooking-row"
                                                style={{
                                                    display: 'grid',
                                                    gridAutoFlow: 'column',
                                                    gridAutoColumns: '44px',
                                                    gap: 4,
                                                    width: 'max-content'
                                                }}
                                            >
                                                {allDates.map((date, index) => {
                                                    const seg = lane.find((it) => it._start === index);
                                                    if (!seg) {
                                                        const covered = lane.some((it) => it._start < index && it._end >= index);
                                                        if (covered) return null;
                                                        return (
                                                            <div
                                                                key={`over-gap-${laneIdx}-${index}`}
                                                                style={{height: 50}}
                                                            />
                                                        );
                                                    }
                                                    const span = Math.max(1, seg._end - seg._start + 1);
                                                    const grp = seg._group;
                                                    const count = grp.items.length;
                                                    const single = count === 1 ? grp.items[0] : null;
                                                    const label = count > 1
                                                        ? `Овербукінг — ${count} шт`
                                                        : `Овербукінг — ${`${single?.first_name || ''} ${single?.last_name || ''}`.trim()}`;

                                                    return (
                                                        <div
                                                            key={`overchip-${laneIdx}-${index}`}
                                                            data-over="1"
                                                            style={{
                                                                gridColumn: `span ${span}`,
                                                                height: 50,
                                                                display: 'flex',
                                                                alignItems: 'stretch'
                                                            }}
                                                            onClick={(ev) => {
                                                                ev.stopPropagation();
                                                                if (count > 1) {
                                                                    if (typeof onOpenOverList === 'function') onOpenOverList(grp.items);
                                                                    return;
                                                                }
                                                                const sel = [];
                                                                for (let i = grp._start; i <= grp._end; i++) {
                                                                    sel.push({
                                                                        date: format(allDates[i], 'yyyy-MM-dd'),
                                                                        objId: obj.id
                                                                    });
                                                                }
                                                                setSelectedCells(sel);
                                                                setSelectedObjId(obj.id);
                                                                setAnchorEl(ev.currentTarget);
                                                                setPopupMode('booking-over');
                                                                setClickedBookingId(single.id);
                                                            }}
                                                        >
                                                            <div
                                                                data-over="1"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    background: '#009e3d',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    padding: '0 6px',
                                                                    boxSizing: 'border-box',
                                                                    borderRadius: 6,
                                                                    position: 'relative'
                                                                }}
                                                                title={label}
                                                            >
                                                                <Typography component="span" variant="caption" style={{
                                                                    color: 'white',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {label}
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                        </div>
                    </div>
                );
            })}
        </>
    );
};

const RenderTableRows = ({
                             data,
                             allDates,
                             handleCellClick,
                             handleLabelClick,
                             selectedCells,
                             setSelectedCells,
                             setAnchorEl,
                             setPopupMode,
                             setClickedBookingId,
                             setSelectedObjId,
                             navigate,
                             onOpenOverList,
                             // Pass other needed state/setters if required
                         }) => {
    // If URL filter range is wider than provided allDates, expand to it (keeps existing behavior otherwise)
    (() => {
        const urlDates = getFilterDatesFromURL();
        if (urlDates && urlDates.length > (allDates?.length || 0)) {
            // eslint-disable-next-line no-param-reassign
            allDates = urlDates;
        }
    })();
    const getEffectiveAllDates = (allDates) => {
        const urlDates = getFilterDatesFromURL();
        if (urlDates && urlDates.length > (allDates?.length || 0)) {
            return urlDates;
        }
        return allDates;
    };
    let overallRowIndex = 0;
    const mod_data = flattenObjects(data);
    // helper to detect overbooking
    const toStr = (v) => (v != null ? String(v) : '');
    const isOver = (b) => toStr(b?.status) === '6' || toStr(b?.booking_system_from) === '4';
    const isExternal = (b) => toStr(b?.booking_system_from) === '2' || toStr(b?.booking_system_from) === '3';
    return mod_data.map((obj, rowIndex) => {
        overallRowIndex++;
        if (obj.children_obj?.length > 0) {
            return (<tr key={overallRowIndex} style={{backgroundColor: '#EEEEEE'}}>
                <td style={{textAlign: 'start', textDecoration: 'underline', cursor: 'pointer'}}
                    onClick={(ev) => handleLabelClick(ev, obj.id)}
                >{obj.name} <span style={{fontSize: '0.8em'}}>({obj.service_name})</span>
                </td>
                <td colSpan={allDates.length}/>
            </tr>);
        }
        // --- NEW: Two rows per object: main for real bookings, second row for overbooking visualization ---
        return (
            <React.Fragment key={overallRowIndex}>
                {/* MAIN ROW: реальні броні та звичайні клітинки */}
                <tr>
                    <td className='object_title' data-x={overallRowIndex} data-pricesindex={rowIndex}
                        style={{
                            position: 'sticky',
                            paddingLeft: obj.ctype === 'hotel_children' ? '24px' : '0px',
                            cursor: obj.ctype === 'hotel_children' ? 'default' : 'pointer',
                            textDecoration: obj.ctype === 'hotel_children' ? '' : 'underline'
                        }}
                        onClick={(ev) => obj.ctype === 'hotel' && handleLabelClick(ev, obj.id)}
                    >
                        {obj.title || obj.name} <span style={{fontSize: '0.8em'}}>({obj.service_name})</span>
                    </td>

                    {(() => {
                        const cells = [];
                        // --- Helper setup for booking segment shape (per object row, not per cell) ---
                        const dayKeyFromISO = (iso) => {
                            const day = parseBookingCalendarDay(iso);
                            return day ? format(day, 'yyyy-MM-dd') : null;
                        };
                        const realBookingsAll = (obj.bookings || []).filter(isActiveVisualBooking);
                        const hasLeftNeighbor = (b) => {
                            const currentFrom = dayKeyFromISO(b.booking_from);
                            if (!currentFrom) return false;
                            return realBookingsAll.some((other) => other.id !== b.id && dayKeyFromISO(other.booking_to) === currentFrom);
                        };
                        const hasRightNeighbor = (b) => {
                            const currentTo = dayKeyFromISO(b.booking_to);
                            if (!currentTo) return false;
                            return realBookingsAll.some((other) => other.id !== b.id && dayKeyFromISO(other.booking_from) === currentTo);
                        };
                        for (let index = 0; index < allDates.length; index++) {
                            const date = allDates[index];

                            const overlapping = obj.bookings?.filter(({booking_from, booking_to}) => {
                                const range = getVisualBookingRange(booking_from, booking_to);
                                if (!range.valid) return false;

                                return !isBefore(date, range.startDay) && !isAfter(date, range.visualEndDay);
                            }) || [];

                            const isReal = isActiveVisualBooking;
                            const realBooking = overlapping.find(isReal) || null;

                            const isEndMonth = isLastDayOfMonth(date);
                            const isSelected = selectedCells.some(
                                cell => cell.date === format(date, 'yyyy-MM-dd') && String(cell.objId) === String(obj.id)
                            );
                            const className = ['selectable'];
                            if (isEndMonth) className.push('end_of_month');
                            if (isSelected) className.push('selected');

                            const spanFor = (b) => {
                                const range = getVisualBookingRange(b.booking_from, b.booking_to);
                                if (!range.valid) return 1;
                                const s = range.startDay;
                                const eVisual = range.visualEndDay;

                                const visStart = isBefore(s, allDates[0]) ? allDates[0] : s;
                                const visEnd = isAfter(eVisual, allDates[allDates.length - 1])
                                    ? allDates[allDates.length - 1]
                                    : eVisual;

                                return Math.max(1, differenceInCalendarDays(visEnd, visStart) + 1);
                            };
                            const firstColW = 260;        // ширина колонки с названием объекта
                            const dayColW = 44;           // ширина колонки дня

                            if (!realBooking) {
                                cells.push(
                                    <td
                                        key={`day-${format(date, 'yyyy-MM-dd')}`}
                                        className={className.join(' ')}
                                        data-objid={obj.id}
                                        data-type={obj.ctype}
                                        data-date={format(date, 'yyyy-MM-dd')}
                                        data-x={overallRowIndex}
                                        data-y={index}
                                        onMouseDown={(ev) => handleCellClick(ev, obj.id, 'start', format(date, 'yyyy-MM-dd'))}
                                        onMouseMove={(ev) => handleCellClick(ev, obj.id, 'move', format(date, 'yyyy-MM-dd'))}
                                        onMouseUp={(ev) => handleCellClick(ev, obj.id, 'end', format(date, 'yyyy-MM-dd'))}
                                        onTouchStart={(ev) => handleCellClick(ev, obj.id, 'start', format(date, 'yyyy-MM-dd'))}
                                        onTouchMove={(ev) => handleCellClick(ev, obj.id, 'move', format(date, 'yyyy-MM-dd'))}
                                        onTouchEnd={(ev) => handleCellClick(ev, obj.id, 'end', format(date, 'yyyy-MM-dd'))}
                                        style={{
                                            position: 'relative',
                                            minHeight: 48,
                                            padding: 0,
                                            minWidth: 44,
                                            width: dayColW
                                        }}
                                    >
                                        {/* Centered per-day number, same look as mobile */}
                                        <div
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                pointerEvents: 'none',
                                                fontSize: 10,
                                                opacity: 0.75,
                                                color: 'rgba(0,0,0,0.85)',
                                                textShadow: '0 1px 0 rgba(255,255,255,0.6)',
                                                zIndex: 100
                                            }}
                                        >
                                            {format(date, 'dd')}
                                        </div>
                                    </td>
                                );
                                continue;
                            }

                            // старт видимого сегмента
                            const bookingRange = getVisualBookingRange(realBooking.booking_from, realBooking.booking_to);
                            if (!bookingRange.valid) {
                                continue;
                            }
                            const s = bookingRange.startDay;
                            const visibleStart = isBefore(s, allDates[0]) ? allDates[0] : s;
                            const isStart = isSameDay(date, visibleStart);
                            if (!isStart) {
                                // сегмент уже идёт, а мы не в его видимом старте — ячейка будет покрыта colSpan сделанным на старте
                                continue;
                            }

                            const realSpan = spanFor(realBooking);
                            const maxSpan = allDates.length - index;
                            const safeSpan = Math.min(realSpan, maxSpan);

                            cells.push(
                                <td
                                    key={`seg-${realBooking.id}-${format(visibleStart, 'yyyy-MM-dd')}`}
                                    data-x={overallRowIndex}
                                    data-y={index}
                                    colSpan={safeSpan}
                                    data-type={obj.ctype}
                                    data-bookid={realBooking.id}
                                    className={(() => {
                                        // Do not add shape class for closed periods (booking_system_from === '0')
                                        if (realBooking.booking_system_from === '0') {
                                            return `calendarColor${realBooking.booking_system_from}`;
                                        }
                                        const left = hasLeftNeighbor(realBooking);
                                        const right = hasRightNeighbor(realBooking);
                                        const shape = (left && right) ? '' : (left ? '' : (right ? '' : ''));
                                        return `${shape} calendarColor${realBooking.booking_system_from}`;
                                    })()}
                                    style={{
                                        position: 'relative',
                                        padding: 0,
                                        minHeight: 48,
                                        minWidth: 44,
                                        width: dayColW
                                    }}
                                    onTouchStart={(ev) => {
                                        ev.stopPropagation();
                                        setSelectedCells([]);
                                        setAnchorEl(ev.currentTarget);
                                        setPopupMode(isExternal(realBooking) ? 'booking-external' : 'booking');
                                        setClickedBookingId(realBooking.id);
                                        setSelectedObjId(obj.id);
                                    }}
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        setSelectedCells([]);
                                        setAnchorEl(ev.currentTarget);
                                        setPopupMode(isExternal(realBooking) ? 'booking-external' : 'booking');
                                        setClickedBookingId(realBooking.id);
                                        setSelectedObjId(obj.id);
                                    }}
                                >
                                    {/* Transparent per-day numbers overlay across the spanned booking */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'grid',
                                            gridTemplateColumns: `repeat(${safeSpan}, 1fr)`,
                                            alignItems: 'center',
                                            padding: '0 4px',
                                            pointerEvents: 'none',
                                            zIndex: 6
                                        }}
                                    >
                                        {Array.from({length: safeSpan}).map((_, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    textAlign: 'center',
                                                    fontSize: 10,
                                                    opacity: 0.8,
                                                    color: 'rgba(0,0,0,0.85)',
                                                    textShadow: '0 1px 0 rgba(255,255,255,0.6)'
                                                }}
                                            >
                                                {format(addDays(date, i), 'dd')}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Bottom ribbon label (guest or closed period) */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: 6,
                                            right: 6,
                                            bottom: 0.5,
                                            padding: '2px 6px',
                                            borderRadius: 4,
                                            background: 'rgba(255,255,255,0.85)',
                                            backdropFilter: 'saturate(150%) blur(2px)',
                                            fontSize: 12,
                                            lineHeight: 0.5,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            zIndex: 5
                                        }}
                                    >
                                        {realBooking.booking_system_from === '0'
                                            ? 'Період закрито'
                                            : `${realBooking.first_name} ${realBooking.last_name}`}
                                    </div>
                                </td>
                            );

                            // ПРОПУСК: перескакиваем покрытые дни
                            index += safeSpan - 1;
                        }
                        return cells;
                    })()}
                </tr>

                {/* OVER-ROWS: под номером — динамическое количество рядов (по числу параллельних овербукингів) */}
                {(() => {
                    const rawOver = (obj.bookings || []).filter(isOver);
                    // если нет овербукингу — вставляем одну «пустую» широкую строку-заглушку
                    if (!rawOver.length) {
                        return (
                            <tr className="overbooking-row ghost">
                                <td style={{height: 20, padding: 0}}/>
                                <td colSpan={allDates.length} style={{height: 50, padding: 0}}/>
                            </tr>
                        );
                    }

                    // иначе — как было: строим lanes и рисуем реальні смужки
                    const withIdx = rawOver
                        .map((b) => {
                            const [s, e] = segmentIndices(b, allDates);
                            return {...b, _start: s, _end: e};
                        })
                        .filter((b) => b._start >= 0 && b._end >= b._start);

                    if (!withIdx.length) {
                        return (
                            <tr className="overbooking-row ghost">
                                <td style={{height: 20, padding: 0}}/>
                                <td colSpan={allDates.length} style={{height: 50, padding: 0}}/>
                            </tr>
                        );
                    }

                    const byPeriod = new Map();
                    withIdx.forEach((it) => {
                        const key = `${it._start}-${it._end}`;
                        const bucket = byPeriod.get(key) || {_start: it._start, _end: it._end, items: []};
                        bucket.items.push(it);
                        byPeriod.set(key, bucket);
                    });

                    const periodGroups = Array.from(byPeriod.values())
                        .sort((a, b) => a._start - b._start || a._end - b._end);

                    const lanes = buildOverbookingLanes(periodGroups.map(g => ({
                        id: g.items[0]?.id,
                        booking_from: g.items[0]?.booking_from,
                        booking_to: g.items[0]?.booking_to,
                        _start: g._start,
                        _end: g._end,
                        _group: g
                    })), allDates);

                    if (!lanes.length) {
                        return (
                            <tr className="overbooking-row ghost">
                                <td style={{height: 20, padding: 0}}/>
                                <td colSpan={allDates.length} style={{height: 50, padding: 0}}/>
                            </tr>
                        );
                    }

                    return lanes.map((lane, laneIdx) => (
                        <tr key={`overlane-${overallRowIndex}-${laneIdx}`} className="overbooking-row">
                            <td style={{height: 50, padding: 0}}/>
                            {allDates.map((date, index) => {
                                const seg = lane.find((it) => it._start === index);
                                if (!seg) {
                                    const covered = lane.some((it) => it._start < index && it._end >= index);
                                    return covered ? null : <td key={index} style={{height: 50, padding: 0}}/>;
                                }
                                const span = Math.max(1, seg._end - seg._start + 1);
                                const grp = seg._group;
                                const count = grp.items.length;
                                const single = count === 1 ? grp.items[0] : null;
                                const label = count > 1
                                    ? `Овербукінг — ${count} шт`
                                    : `Овербукінг — ${`${single?.first_name || ''} ${single?.last_name || ''}`.trim()}`;

                                return (
                                    <td
                                        key={index}
                                        colSpan={span}
                                        style={{position: 'relative', padding: 0, height: 50}}
                                        onClick={(ev) => {
                                            if (count > 1) {
                                                if (typeof onOpenOverList === 'function') onOpenOverList(grp.items);
                                                return;
                                            }
                                            const sel = [];
                                            for (let i = grp._start; i <= grp._end; i++) {
                                                sel.push({date: format(allDates[i], 'yyyy-MM-dd'), objId: obj.id});
                                            }
                                            setSelectedCells(sel);
                                            setSelectedObjId(obj.id);
                                            setAnchorEl(ev.currentTarget);
                                            setPopupMode('booking-over');
                                            setClickedBookingId(single.id);
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                background: '#009e3d',
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '0 6px',
                                                boxSizing: 'border-box',
                                                position: 'relative'
                                            }}
                                            title={label}
                                        >
                                            <Typography component="span" variant="caption" style={{
                                                color: 'white',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {label}
                                            </Typography>
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ));
                })()}

            </React.Fragment>
        );
    });
};

const CustomTable = ({data, startDate, endDate, onRefresh}) => {
    const allDates = getDatesBetween(startDate, endDate);
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:1200px)');

    const renderTableHeader = () => {
        const monthNames = [];
        let currentMonth = startDate;

        while (isBefore(currentMonth, endDate) || isSameDay(currentMonth, endDate)) {
            const monthName = format(currentMonth, 'LLL', {locale: uk});

            // Clamp the visible range for this month to [currentMonth, min(endOfMonth, endDate)] inclusive
            const monthEnd = endOfMonth(currentMonth);
            const visibleEnd = isBefore(endDate, monthEnd) ? endDate : monthEnd;
            const remainingDays = differenceInDays(visibleEnd, currentMonth) + 1; // inclusive of current day

            monthNames.push({name: monthName, days: remainingDays});

            // move to the first day of the next month
            currentMonth = startOfMonth(addMonths(currentMonth, 1));
        }

        // Safety check to warn if header days and allDates mismatch
        try {
            const headerDays = monthNames.reduce((sum, m) => sum + m.days, 0);
            if (headerDays !== allDates.length) {
                // eslint-disable-next-line no-console
                console.warn('[calendarAll] Month header days (', headerDays, ') do not match allDates length (', allDates.length, ').');
            }
        } catch (_) {
        }

        return (
            <React.Fragment>
                <tr>
                    <th colSpan={allDates.length + 1}>{`${format(startDate, 'dd MMMM', {locale: uk})} - ${format(endDate, 'dd MMMM yyyy', {locale: uk})}`}</th>
                </tr>
                <tr>
                    <th></th>
                    {monthNames.map((el, index) => (
                        <th key={index} colSpan={el.days} style={{textAlign: 'left'}}>{el.name}</th>
                    ))}
                </tr>
                <tr>
                    <th></th>
                    {allDates.map((date, index) => (
                        <th className="normal-th" key={index} style={{width: dayColW, whiteSpace: 'nowrap'}}>
                            {format(date, 'dd')}
                        </th>
                    ))}
                </tr>
            </React.Fragment>
        );
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const [popupMode, setPopupMode] = useState(null);
    const [clickedID, setClickedId] = useState(null);
    const [clickedBookingId, setClickedBookingId] = useState(null);
    const [selecting, setSelecting] = useState(false);
    const [startCell, setStartCell] = useState(null);
    const [selectedCells, setSelectedCells] = useState([]);
    const [selectedObjId, setSelectedObjId] = useState(null); // Новое состояние для ID выбранного под-объекта
    const [showModal, setShowModal] = useState(false);
    // State for ModalForm endpoint/form name
    const [modalFormName, setModalFormName] = useState('BookingForm');
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [bookingDetails, setBookingDetails] = useState(null);
    // ModalForm initial values override
    const [initialValuesOverride, setInitialValuesOverride] = useState(null);
    // Edit modal state
    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [editBookingId, setEditBookingId] = useState(null);
    // values-only state for selected editable fields
    const [editValues, setEditValues] = useState(null);
    const selectedFrom = React.useMemo(
        () => selectedCells?.[0]?.date || startCell?.date || null,
        [selectedCells, startCell]
    );
    const selectedTo = React.useMemo(
        () => (selectedCells?.length ? selectedCells[selectedCells.length - 1]?.date : startCell?.date) || null,
        [selectedCells, startCell]
    );

    // multi-overbooking list modal
    const [overListOpen, setOverListOpen] = useState(false);
    const [overListItems, setOverListItems] = useState([]);

    // Memoized count of actions in the popper for sizing
    const actionsCount = React.useMemo(() => {
        switch (popupMode) {
            case 'book':
                return 2; // Забронювати + Закрити для бронювання
            case 'booking':
                return 4; // Овербукинг + Деталі + Редагувати
            case 'booking-over':
                return 2; // Деталі + Прибрати з овербукінгу
            case 'booking-external':
                return 1;
            case 'info':
                return 2; // Детали об объекта + Календарь
            default:
                return 1;
        }
    }, [popupMode]);
    // fields we actually allow to edit in the modal
    const allowedEditFields = ['booking_from', 'booking_to', 'first_name', 'last_name', 'email', 'phone', 'comments', 'time_arrived'];

    const handleOpenModal = () => {
        setModalFormName('BookingForm');
        setShowModal(true);
    };

    const openQuickBooking = (anchorElEl, objId, date) => {
        setInitialValuesOverride(null);
        setSelectedCells([{objId: String(objId), date}]); // from = to = выбранный день
        setSelectedObjId(String(objId));
        setAnchorEl(anchorElEl);       // чтобы Popper смонтировался (внутри него ModalForm)
        setPopupMode('book');          // режим «кнопки брони»
        setModalFormName('BookingForm');
        // no setShowModal(true) here; let ModalForm open from button
    };
    // Helper to open booking modal with "close period" defaults (for mobile)
    const openClosePeriodModal = () => {
        const startPeriod = selectedCells[0]?.date || selectedFrom;
        const endPeriod = selectedCells[selectedCells.length - 1]?.date || selectedTo;
        const obj_id =
            selectedCells?.[0]?.objId ||
            startCell?.objId ||
            selectedObjId;
        if (!startPeriod || !endPeriod || !obj_id) {
            alert("Выберите даты для закрытия периода.");
            return;
        }
        const params = new URLSearchParams({
            default_booking_from: startPeriod,
            default_booking_to: endPeriod,
            default_email: 'internal_close@bit-ua.com',
            default_first_name: 'Період закрито.',
            default_last_name: `Ким: (${AuthProvider.user?.username || "менеджер"})`,
            default_phone: '+380000000000',
            default_hotel_children: String(obj_id),
            default_check_info: 'true',
            default_time_arrived: `${format(new Date(), 'HH')}-00`,
            default_booking_system_from: '0'
        });
        setInitialValuesOverride(null);
        setModalFormName(`BookingFormInit/?${params.toString()}`);
        setPopupMode('book');
        setShowModal(true);
    };

    // helpers
    const norm = (v) => (v === null || v === undefined ? '' : v);
    const isoToLocalInput = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const openBookingDetails = async (bookingId) => {
        if (!bookingId) return;
        try {
            setDetailsLoading(true);
            const res = await api.get(`/api/booking_modules/use/booking/admin_book/${bookingId}/`);
            if (res && res.status === 200 && res.data && res.data.data) {
                setBookingDetails(res.data.data);
                setDetailsOpen(true);
                setAnchorEl(null);
            } else {
                showSnackbar('Не вдалося отримати деталі бронювання', 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            }
        } catch (e) {
            showSnackbar(`Помилка отримання деталей бронювання: ${e}`, 'error', {
                sx: {
                    backgroundColor: '#ff0000',
                    color: 'white'
                }
            });
        } finally {
            setDetailsLoading(false);
        }
    };

    const openEditModal = async (bookingId) => {
        if (!bookingId) return;
        try {
            setEditLoading(true);
            setEditBookingId(bookingId);
            const res = await api.get(`api/booking/child/${bookingId}/0/`);
            if (res && res.status === 200 && res.data && res.data.form) {
                const schema = res.data.form;
                // keep only allowed fields in the edit form
                const filteredSchema = Object.fromEntries(
                    Object.entries(schema).filter(([k]) => allowedEditFields.includes(k))
                );
                setEditForm(filteredSchema);
                // build initial values for inputs
                const values = {};
                allowedEditFields.forEach((k) => {
                    if (!filteredSchema[k]) return;
                    const field = filteredSchema[k];
                    if (field.type === 'DateTimeInput') {
                        values[k] = isoToLocalInput(field.initial);
                    } else {
                        values[k] = norm(field.initial);
                    }
                });
                setEditValues(values);
                setEditOpen(true);
                setAnchorEl(null);
            } else {
                showSnackbar('Не вдалося отримати форму редагування', 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            }
        } catch (e) {
            showSnackbar(`Помилка отримання форми редагування: ${e}`, 'error', {
                sx: {
                    backgroundColor: '#ff0000',
                    color: 'white'
                }
            });
        } finally {
            setEditLoading(false);
        }
    };

    const handleEditChange = (key, value) => {
        setEditValues(prev => ({...prev, [key]: value}));
    };

    const handleEditSubmit = async () => {
        if (!editBookingId || !editValues) return;
        try {
            const formData = jsonToFormData(editValues);
            const response = await api.patch(`api/booking/child/${editBookingId}/0/`, formData);
            if (response.status === 200) {
                showSnackbar('Бронювання успішно оновлено!', 'success', {
                    sx: {
                        backgroundColor: '#1de416',
                        color: 'white'
                    }
                });
                setEditOpen(false);
                setTimeout(async () => {
                    if (typeof onRefresh === 'function') {
                        try {
                            await onRefresh();
                        } catch (e) {
                            console.warn('onRefresh failed, fallback to reload', e);
                            window.location.href = '/calendar';
                        }
                    } else {
                        window.location.href = '/calendar';
                    }
                }, 3000);
            } else {
                showSnackbar('Помилка при оновлені бронювання', 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            }
        } catch (e) {
            if (e.response && e.response.status === 409) {
                showSnackbar('На обрані дати вже є бронювання', 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            } else {
                showSnackbar(`Помилка при оновлені бронювання ${e}`, 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            }
        }
    };

    const open = Boolean(anchorEl);

    const openOverList = (items) => {
        setOverListItems(items || []);
        setOverListOpen(true);
        setAnchorEl(null);
    };

    const handleRemoveOverbooking = async (bookingId) => {
        if (!bookingId) return;
        const ok = window.confirm('Ви збираєтеся повернути бронювання з категорії "овербукінг" до звичайної.\nВи впевнені?');
        if (!ok) return;
        try {
            const url = `/api/booking_modules/use/booking/update-status/${bookingId}/4/1/`;
            const res = await api.patch(url, {status: 4, statusBook: 1});
            if (res && (res.status === 200 || res.status === 204)) {
                showSnackbar('Статус оновлено', 'success', {sx: {backgroundColor: '#1de416', color: 'white'}});
                setOverListOpen(false);
                setTimeout(async () => {
                    if (typeof onRefresh === 'function') {
                        try {
                            await onRefresh();
                        } catch (e) {
                            console.warn('onRefresh failed, fallback to reload', e);
                            window.location.reload();
                        }
                    } else {
                        window.location.reload();
                    }
                }, 2000);
            } else {
                showSnackbar('Помилка при оновлені статусу', 'error', {
                    sx: {
                        backgroundColor: '#ff0000',
                        color: 'white'
                    }
                });
            }
        } catch (e) {
            showSnackbar(`Помилка при оновлені статусу ${e}`, 'error', {
                sx: {
                    backgroundColor: '#ff0000',
                    color: 'white'
                }
            });
        }
    };

    const handlePopoverClose = () => setAnchorEl(null);

    const handleCellClick = (event, objId, action, date) => {
        if (action === 'start') {
            setSelecting(true);
            setStartCell({objId: String(objId), date});
            setSelectedCells([{objId: String(objId), date}]);
            setSelectedObjId(String(objId));
        } else if (action === 'move' && selecting) {

            if (String(startCell?.objId) !== String(objId)) {
                return;
            }

            updateSelectedCells(startCell, {
                objId: String(objId),
                date
            });

        } else if (action === 'end') {

            if (String(startCell?.objId) !== String(objId)) {
                return;
            }

            if (startCell?.date && date) {
                updateSelectedCells(startCell, {
                    objId: String(objId),
                    date
                });
            }

            setSelecting(false);

            // гарантированно сохраняем правильный объект
            setSelectedObjId(String(startCell.objId));

            setAnchorEl(event.currentTarget);
            setPopupMode('book');
        }
    };

    const updateSelectedCells = (start, end) => {
        if (!start?.date || !end?.date) return;
        const sIdx = allDates.findIndex(d => format(d, 'yyyy-MM-dd') === start.date);
        const eIdx = allDates.findIndex(d => format(d, 'yyyy-MM-dd') === end.date);
        if (sIdx === -1 || eIdx === -1) return; // ничего не делаем, если дата не попала в окно

        const lo = Math.min(sIdx, eIdx);
        const hi = Math.max(sIdx, eIdx);
        const range = allDates.slice(lo, hi + 1);
        const cells = range.map(d => ({date: format(d, 'yyyy-MM-dd'), objId: String(start.objId)}));
        setSelectedCells(cells);
    };

    const handleLabelClick = (ev, id) => {
        setAnchorEl(ev.currentTarget);
        setClickedId(id);
        setPopupMode('info');
    };

    const handleClosePeriod = async () => {
        // Open the close-period form immediately; do NOT close the popper first,
        // because ModalForm is rendered inside the Popper and would unmount.
        openClosePeriodModal();
    };

    const handleBookingSuccess = async () => {
        showSnackbar("Бронювання створено", "success", {
            sx: {backgroundColor: "#1de416", color: "white"}
        });
        setShowModal(false);
        setAnchorEl(null);
        setInitialValuesOverride(null);
        setTimeout(async () => {
            if (typeof onRefresh === 'function') {
                try {
                    await onRefresh();
                } catch (e) {
                    console.warn('onRefresh failed, fallback to reload', e);
                    window.location.reload();
                }
            } else {
                window.location.reload();
            }
        }, 2000);
    };

    const selectedChildId = React.useMemo(
        () => selectedCells?.[0]?.objId || startCell?.objId || selectedObjId,
        [selectedCells, startCell, selectedObjId]
    );

    const initialValues = initialValuesOverride || {
        default_booking_from: selectedFrom,
        default_booking_to: selectedTo,
        default_hotel_children: selectedChildId,
        default_status: "4",
        default_booking_system_from: '0'
    };

    const firstColW = 260;        // ширина колонки с названием объекта
    const dayColW = 44;           // ширина колонки дня

    return (
        <div
            className="table-container"
            style={{
                width: '100%',
                overflowX: isMobile ? 'hidden' : 'auto',
                overflowY: 'hidden'
            }}
        >
            {isMobile ? (
                <div>
                    {/* Mobile header */}
                    <div style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid #eee',
                        position: 'sticky',
                        top: 0,
                        background: 'white',
                        zIndex: 6
                    }}>
                        <Typography variant="subtitle2">
                            {`${format(startDate, 'dd MMMM', {locale: uk})} — ${format(endDate, 'dd MMMM yyyy', {locale: uk})}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Проведіть пальцем по датам, щоб обрати
                            період</Typography>
                    </div>

                    {/* Mobile rows */}
                    <MobileRows
                        data={data}
                        allDates={allDates}
                        handleCellClick={handleCellClick}
                        handleLabelClick={handleLabelClick}
                        selectedCells={selectedCells}
                        setSelectedCells={setSelectedCells}
                        setAnchorEl={setAnchorEl}
                        setPopupMode={setPopupMode}
                        setClickedBookingId={setClickedBookingId}
                        setSelectedObjId={setSelectedObjId}
                        navigate={navigate}
                        onOpenOverList={openOverList}
                        openQuickBooking={openQuickBooking}   // ← ДОБАВЬ ЭТО
                    />
                </div>
            ) : (
                <table className='desktop' style={{
                    width: 'fit-content',
                    tableLayout: 'fixed',
                    borderCollapse: 'separate',
                    borderSpacing: 0
                }}>
                    <colgroup>
                        <col style={{width: firstColW}}/>
                        {allDates.map((_, i) => <col key={i} style={{width: dayColW}}/>)}
                    </colgroup>
                    <thead>{renderTableHeader()}</thead>
                    <tbody>
                    <RenderTableRows
                        data={data}
                        allDates={allDates}
                        handleCellClick={handleCellClick}
                        handleLabelClick={handleLabelClick}
                        selectedCells={selectedCells}
                        setSelectedCells={setSelectedCells}
                        setAnchorEl={setAnchorEl}
                        setPopupMode={setPopupMode}
                        setClickedBookingId={setClickedBookingId}
                        setSelectedObjId={setSelectedObjId}
                        navigate={navigate}
                        onOpenOverList={openOverList}
                    />
                    </tbody>
                </table>
            )}

            <Popper
                open={open}
                anchorEl={anchorEl}
                placement={isMobile ? 'bottom-start' : 'bottom-start'}
                modifiers={[
                    {name: 'preventOverflow', options: {boundary: 'viewport', padding: 8}},
                    {name: 'flip', options: {fallbackPlacements: ['bottom', 'top', 'right', 'left']}},
                    {name: 'offset', options: {offset: [0, 8]}},
                ]}
                style={{zIndex: 1300}}
            >
                <Paper
                    elevation={4}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        width: 'auto',
                        minWidth: actionsCount ? actionsCount * 48 + 8 : undefined, // ~48px per icon + padding
                        maxWidth: '96vw',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        p: 1,
                        boxSizing: 'border-box',
                        borderRadius: 2
                    }}
                >
                    <Stack direction='row'>
                        {popupMode === 'book' ? (
                            // как было: Забронювати + Закрити для бронювання
                            <>
                                <Tooltip title="Забронювати" placement='top' arrow>
                                    <IconButton onClick={handleOpenModal}>
                                        <LockIcon/>
                                    </IconButton>
                                </Tooltip>
                                <ModalForm
                                    key={`${selectedFrom || ''}-${selectedTo || ''}-${selectedObjId || ''}-${modalFormName}`}
                                    formName={modalFormName}
                                    show={showModal}
                                    setShowModal={setShowModal}
                                    initialValues={initialValues}
                                    onSuccess={handleBookingSuccess}
                                />
                                <Tooltip title="Закрити для бронювання" placement='top' arrow>
                                    <IconButton onClick={() => {
                                        openClosePeriodModal();
                                    }}>
                                        <DoDisturbIcon/>
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : popupMode === 'booking' ? (
                            // обычная бронь: Овербукинг + Деталі + Редагувати
                            <>
                                <Tooltip title="Овербукинг" placement='top' arrow>
                                    <IconButton onClick={async () => {
                                        if (!clickedBookingId) return;
                                        const ok = window.confirm('Ви збираєтеся перенести бронювання в категорію "овербукінг".\nВи впевнені?');
                                        if (!ok) {
                                            return; // відміна користувачем
                                        }
                                        try {
                                            const url = `/api/booking_modules/use/booking/update-status/${clickedBookingId}/6/4/`;
                                            const res = await api.patch(url, {status: 6, statusBook: 4});
                                            if (res && (res.status === 200 || res.status === 204)) {
                                                showSnackbar("Статус оновлено", "success", {
                                                    sx: {
                                                        backgroundColor: "#1de416",
                                                        color: "white"
                                                    }
                                                });
                                                setAnchorEl(null);
                                                setTimeout(async () => {
                                                    if (typeof onRefresh === 'function') {
                                                        try {
                                                            await onRefresh();
                                                        } catch (e) {
                                                            console.warn('onRefresh failed, fallback to reload', e);
                                                            window.location.reload();
                                                        }
                                                    } else {
                                                        window.location.reload();
                                                    }
                                                }, 2000);
                                            } else {
                                                showSnackbar('Помилка при оновлені статусу', 'error', {
                                                    sx: {
                                                        backgroundColor: "#ff0000",
                                                        color: "white"
                                                    }
                                                });
                                            }
                                        } catch (e) {
                                            showSnackbar(`Помилка при оновлені статусу ${e}`, 'error', {
                                                sx: {
                                                    backgroundColor: "#ff0000",
                                                    color: "white"
                                                }
                                            });
                                        }
                                    }}>
                                        <LayersOutlinedIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Відкрити період" placement='top' arrow>
                                    <IconButton onClick={async () => {
                                        if (!clickedBookingId) return;
                                        const ok = window.confirm('Ви збираєтеся відкрити період (видалити запис закритого періоду).\nВи впевнені?');
                                        if (!ok) return;
                                        try {
                                            const url = `/api/BookingFormInit/${clickedBookingId}/`;
                                            const res = await api.delete(url);
                                            if (res && (res.status === 200 || res.status === 204 || res.status === 202)) {
                                                showSnackbar('Період відкрито', 'success', {
                                                    sx: {backgroundColor: '#1de416', color: 'white'}
                                                });
                                                setAnchorEl(null);
                                                setTimeout(async () => {
                                                    if (typeof onRefresh === 'function') {
                                                        try {
                                                            await onRefresh();
                                                        } catch (e) {
                                                            console.warn('onRefresh failed, fallback to reload', e);
                                                            window.location.reload();
                                                        }
                                                    } else {
                                                        window.location.reload();
                                                    }
                                                }, 2000);
                                            } else {
                                                showSnackbar('Не вдалося відкрити період', 'error', {
                                                    sx: {backgroundColor: '#ff0000', color: 'white'}
                                                });
                                            }
                                        } catch (e) {
                                            showSnackbar(`Помилка при відкритті періоду ${e}`, 'error', {
                                                sx: {backgroundColor: '#ff0000', color: 'white'}
                                            });
                                        }
                                    }}>
                                        <DoDisturbOffIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Переглянути деталі" placement='top' arrow>
                                    <IconButton onClick={() => openBookingDetails(clickedBookingId)}>
                                        <VisibilityOutlinedIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Редагувати" placement='top' arrow>
                                    <IconButton
                                        onClick={() => openEditModal(clickedBookingId)}
                                        sx={{'&:hover': {backgroundColor: '#DCEDFF', color: 'black'}}}
                                    >
                                        <EditOutlinedIcon/>
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : popupMode === 'booking-over' ? (
                            // клік по овербукингу: перегляд деталей + зняти з овербукінгу
                            <>
                                <Tooltip title="Переглянути деталі" placement='top' arrow>
                                    <IconButton onClick={() => openBookingDetails(clickedBookingId)}>
                                        <VisibilityOutlinedIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Прибрати з овербукінгу" placement='top' arrow>
                                    <IconButton onClick={async () => {
                                        if (!clickedBookingId) return;
                                        const ok = window.confirm('Ви збираєтеся повернути бронювання з категорії "овербукінг" до звичайної.\nВи впевнені?');
                                        if (!ok) return;
                                        try {
                                            const isBookingICS = (booking) => {
                                                return (
                                                    booking?.email === 'booking-calendar' &&
                                                    booking?.phone === 'booking-ics'
                                                );
                                            };

                                            const isClosedPeriod = (booking) => {
                                                return booking?.first_name === 'Період закрито.';
                                            };

                                            let details = null;

                                            try {
                                                const detailsRes = await api.get(`/api/booking_modules/use/booking/admin_book/${clickedBookingId}/`);
                                                if (detailsRes?.status === 200 && detailsRes?.data?.data) {
                                                    details = detailsRes.data.data;
                                                }
                                            } catch (e) {
                                                console.warn('Не вдалося отримати деталі бронювання перед зміною статусу', e);
                                            }

                                            const isClosed = isClosedPeriod(details);
                                            const isICS = isBookingICS(details);

                                            let url = `/api/booking_modules/use/booking/update-status/${clickedBookingId}/4/1/`;

                                            if (isClosed) {
                                                url = `/api/booking_modules/use/booking/update-status/${clickedBookingId}/4/0/`;
                                            } else if (isICS) {
                                                url = `/api/booking_modules/use/booking/update-status/${clickedBookingId}/4/3/`;
                                            }

                                            const source = isClosed ? 0 : isICS ? 3 : 1;
                                            const res = await api.patch(url, {status: 4, statusBook: source});
                                            if (res && (res.status === 200 || res.status === 204)) {
                                                showSnackbar("Статус оновлено", "success", {
                                                    sx: {
                                                        backgroundColor: "#1de416",
                                                        color: "white"
                                                    }
                                                });
                                                setAnchorEl(null);
                                                setTimeout(async () => {
                                                    if (typeof onRefresh === 'function') {
                                                        try {
                                                            await onRefresh();
                                                        } catch (e) {
                                                            console.warn('onRefresh failed, fallback to reload', e);
                                                            window.location.reload();
                                                        }
                                                    } else {
                                                        window.location.reload();
                                                    }
                                                }, 2000);
                                            } else {
                                                showSnackbar('Помилка при оновлені статусу', 'error', {
                                                    sx: {
                                                        backgroundColor: "#ff0000",
                                                        color: "white"
                                                    }
                                                });
                                            }
                                        } catch (e) {
                                            showSnackbar(`Помилка при оновлені статусу ${e}`, 'error', {
                                                sx: {
                                                    backgroundColor: "#ff0000",
                                                    color: "white"
                                                }
                                            });
                                        }
                                    }}>
                                        <UndoOutlinedIcon/>
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : popupMode === 'booking-external' ? (
                            <>
                                <Tooltip title="Переглянути деталі" placement='top' arrow>
                                    <IconButton onClick={() => openBookingDetails(clickedBookingId)}>
                                        <VisibilityOutlinedIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Ігнорувати це бронювання" placement='top' arrow>
                                    <IconButton onClick={async () => {
                                        if (!clickedBookingId) return;
                                        const ok = window.confirm('Ви збираєтесь ігнорувати це бронювання з зовнішньої системи.\nВи впевнені?');
                                        if (!ok) return;
                                        try {
                                            const url = `/api/booking_modules/use/booking/update-status/${clickedBookingId}/6/4/`;
                                            const res = await api.patch(url, {status: 6, statusBook: 4});
                                            if (res && (res.status === 200 || res.status === 204)) {
                                                showSnackbar("Статус оновлено", "success", {
                                                    sx: {
                                                        backgroundColor: "#1de416",
                                                        color: "white"
                                                    }
                                                });
                                                setAnchorEl(null);
                                                setTimeout(async () => {
                                                    if (typeof onRefresh === 'function') {
                                                        try {
                                                            await onRefresh();
                                                        } catch (e) {
                                                            console.warn('onRefresh failed, fallback to reload', e);
                                                            window.location.reload();
                                                        }
                                                    } else {
                                                        window.location.reload();
                                                    }
                                                }, 2000);
                                            } else {
                                                showSnackbar('Помилка при оновлені статусу', 'error', {
                                                    sx: {
                                                        backgroundColor: "#ff0000",
                                                        color: "white"
                                                    }
                                                });
                                            }
                                        } catch (e) {
                                            showSnackbar(`Помилка при оновлені статусу ${e}`, 'error', {
                                                sx: {
                                                    backgroundColor: "#ff0000",
                                                    color: "white"
                                                }
                                            });
                                        }
                                    }}>
                                        <AirplanemodeInactiveIcon/>
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            // info ветка
                            <>
                                <Tooltip title="Детали об объекта" placement='top' arrow>
                                    <IconButton onClick={() => navigate(`/object/${clickedID}/details`)}>
                                        <InfoOutlinedIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Календарь" placement='top' arrow>
                                    <IconButton onClick={() => navigate(`/object/${clickedID}/calendar`)}>
                                        <CalendarMonthOutlinedIcon/>
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Stack>
                </Paper>
            </Popper>
            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                fullScreen={false}
                fullWidth
                maxWidth="sm"
                keepMounted
                scroll="paper"
                transitionDuration={{appear: 150, enter: 150, exit: 100}}
                PaperProps={{
                    elevation: 6,
                    sx: {width: {xs: '96vw', sm: 'auto'}, maxWidth: 560, maxHeight: '85vh', m: 1, borderRadius: 2}
                }}
            >
                <DialogTitle>Деталі бронювання</DialogTitle>
                <DialogContent dividers sx={{maxHeight: '65vh', overflow: 'auto'}}>
                    {detailsLoading ? (
                        <Typography variant="body2">Завантаження…</Typography>
                    ) : bookingDetails ? (
                        <Stack spacing={1.2}>
                            <Typography variant="body2"><strong>ID:</strong> {bookingDetails.id}</Typography>
                            <Divider/>
                            <Typography
                                variant="body2"><strong>Від:</strong> {bookingDetails.booking_from ? format(new Date(bookingDetails.booking_from), 'yyyy-MM-dd HH:mm') : '—'}
                            </Typography>
                            <Typography
                                variant="body2"><strong>До:</strong> {bookingDetails.booking_to ? format(new Date(bookingDetails.booking_to), 'yyyy-MM-dd HH:mm') : '—'}
                            </Typography>
                            <Divider/>
                            <Typography variant="body2"><strong>Імʼя:</strong> {bookingDetails.first_name}</Typography>
                            <Typography variant="body2"><strong>Прізвище:</strong> {bookingDetails.last_name}
                            </Typography>
                            <Typography variant="body2"><strong>Email:</strong> {bookingDetails.email}</Typography>
                            <Typography variant="body2"><strong>Телефон:</strong> {bookingDetails.phone}</Typography>
                            <Typography variant="body2"><strong>Коментар:</strong> {bookingDetails.comments || '—'}
                            </Typography>
                            <Typography variant="body2"><strong>Час прибуття:</strong> {bookingDetails.time_arrived}
                            </Typography>
                            <Typography variant="body2"><strong>Ціна:</strong> {bookingDetails.price}</Typography>
                        </Stack>
                    ) : (
                        <Typography variant="body2">Немає даних для відображення</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                fullScreen={false}
                fullWidth
                maxWidth="md"
                keepMounted
                scroll="paper"
                transitionDuration={{appear: 150, enter: 150, exit: 100}}
                PaperProps={{
                    elevation: 6,
                    sx: {width: {xs: '96vw', sm: 'auto'}, maxWidth: 720, maxHeight: '85vh', m: 1, borderRadius: 2}
                }}
            >
                <DialogTitle>Редагування бронювання</DialogTitle>
                <DialogContent dividers sx={{maxHeight: '65vh', overflow: 'auto'}}>
                    {editLoading ? (
                        <Typography variant="body2">Завантаження форми…</Typography>
                    ) : editForm && editValues ? (
                        <Stack spacing={1.2}>
                            {editForm.booking_from && (
                                <TextField
                                    label={editForm.booking_from.label || 'Дата початку бронювання'}
                                    type="datetime-local"
                                    value={editValues.booking_from}
                                    onChange={(e) => handleEditChange('booking_from', e.target.value)}
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{shrink: true}}
                                />
                            )}
                            {editForm.booking_to && (
                                <TextField
                                    label={editForm.booking_to.label || 'Дата кінця бронювання'}
                                    type="datetime-local"
                                    value={editValues.booking_to}
                                    onChange={(e) => handleEditChange('booking_to', e.target.value)}
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{shrink: true}}
                                />
                            )}
                            {editForm.first_name && (
                                <TextField
                                    label={editForm.first_name.label || 'Імʼя'}
                                    value={editValues.first_name}
                                    onChange={(e) => handleEditChange('first_name', e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                            )}
                            {editForm.last_name && (
                                <TextField
                                    label={editForm.last_name.label || 'Прізвище'}
                                    value={editValues.last_name}
                                    onChange={(e) => handleEditChange('last_name', e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                            )}
                            {editForm.email && (
                                <TextField
                                    label={editForm.email.label || 'Пошта'}
                                    value={editValues.email}
                                    onChange={(e) => handleEditChange('email', e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                            )}
                            {editForm.phone && (
                                <TextField
                                    label={editForm.phone.label || 'Телефон'}
                                    value={editValues.phone}
                                    onChange={(e) => handleEditChange('phone', e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                            )}
                            {editForm.comments && (
                                <TextField
                                    label={editForm.comments.label || 'Коментар'}
                                    value={editValues.comments}
                                    onChange={(e) => handleEditChange('comments', e.target.value)}
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={3}
                                />
                            )}
                            {editForm.time_arrived && (
                                <TextField
                                    select
                                    label={editForm.time_arrived.label || 'Час прибуття'}
                                    value={editValues.time_arrived}
                                    onChange={(e) => handleEditChange('time_arrived', e.target.value)}
                                    size="small"
                                    fullWidth
                                >
                                    {(editForm.time_arrived.choices || []).map(([value, label]) => (
                                        <MenuItem key={value} value={value}>{label}</MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </Stack>
                    ) : (
                        <Typography variant="body2">Немає даних форми</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Скасувати</Button>
                    <Button variant="contained" onClick={handleEditSubmit} disabled={!editValues}>Зберегти</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={overListOpen} onClose={() => setOverListOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Овербукінги в цей період</DialogTitle>
                <DialogContent dividers>
                    {(!overListItems || overListItems.length === 0) ? (
                        <Typography variant="body2">Немає елементів</Typography>
                    ) : (
                        <List dense>
                            {overListItems.map((item) => (
                                <ListItem key={item.id}
                                          secondaryAction={
                                              <Stack direction="row" spacing={1}>
                                                  <Tooltip title="Переглянути деталі" placement='top' arrow>
                                                      <IconButton edge="end"
                                                                  onClick={() => openBookingDetails(item.id)}>
                                                          <VisibilityOutlinedIcon/>
                                                      </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Прибрати з овербукінгу" placement='top' arrow>
                                                      <IconButton edge="end"
                                                                  onClick={() => handleRemoveOverbooking(item.id)}>
                                                          <UndoOutlinedIcon/>
                                                      </IconButton>
                                                  </Tooltip>
                                              </Stack>
                                          }
                                >
                                    <ListItemText
                                        primary={`ID ${item.id} — ${(item.first_name || '')} ${(item.last_name || '')}`.trim()}
                                        secondary={`${item.booking_from?.slice(0, 16) || ''} → ${item.booking_to?.slice(0, 16) || ''}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOverListOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CustomTable;
{/* Create booking dialog, if present */
}
{/* If there is a Dialog for showModal, update it for compact modal */
}
{/* This is a guess: check if a Dialog for showModal exists */
}
{/* If not present, ignore this block */
}
